/**
 * ADR-0013 — Stock Projection Owner (Inventory).
 * Public surface for sellable stockNow/value from lots (isLotIncludedInStockMetrics).
 * Implementation may call CalculationFactory math helpers — do not import the factory from UI.
 * stockSnapshots may accelerate KPI when present; operational qty is lot projection, not blob stockData.
 * Pure: callers supply lots; persistence lives outside.
 */

import CalculationFactory from '../../../services/calculationFactory';
import { filterLotsForStore, DEFAULT_STORE_ID } from '../stores/storeCatalog';

/**
 * @param {string|number} productId
 * @param {string|null} [location]
 * @param {Array|object} [lots]
 * @returns {{
 *   stockIn: number,
 *   stockOut: number,
 *   stockNow: number,
 *   value: number,
 *   potentialProfit: number,
 *   lotsCount: number,
 *   activeLotsCount: number
 * }}
 */
export function projectStockFromLots(productId, location = null, lots = []) {
  return CalculationFactory.calculateStockFromLots(productId, location, lots);
}

/**
 * Project stock for one store (lot.storeId; missing storeId → default store).
 */
export function projectStockFromLotsForStore(
  productId,
  location = null,
  lots = [],
  storeId = null,
  defaultStoreId = DEFAULT_STORE_ID
) {
  const scoped = filterLotsForStore(lots, storeId, defaultStoreId);
  return projectStockFromLots(productId, location, scoped);
}

/**
 * Recompute a stock table row for a store scope.
 */
export function reprojectStockRowForStore(item, allLots, storeId) {
  if (!item || !storeId || storeId === 'all') return item;
  const productId = item.productId ?? item.product?.id;
  const location = item.location || 'Principal';
  const proj = projectStockFromLotsForStore(productId, location, allLots, storeId);
  return {
    ...item,
    stockNow: proj.stockNow,
    stock: proj.stockNow,
    stockIn: proj.stockIn,
    stockOut: proj.stockOut,
    value: proj.value,
    potentialProfit: proj.potentialProfit,
    lotsCount: proj.lotsCount,
    activeLotsCount: proj.activeLotsCount,
    storeId
  };
}

/**
 * @param {string|number} productId
 * @param {string|null} [location]
 * @param {Array|object} [lots]
 * @returns {number}
 */
export function getStockNow(productId, location = null, lots = []) {
  return Number(projectStockFromLots(productId, location, lots).stockNow) || 0;
}
